import type {
  ProjectContextDiffSegment,
  ProjectContextGeneralFact,
  ProjectContextVersionDetailResponse,
  ProjectContextVersionListItem,
  ProjectContextVersionSourceKind,
} from '@ai-meeting/shared'

export type { ProjectContextDiffSegment }

/** Labels for non-meeting sources and missing meeting titles. */
export type ProjectContextVersionSourceLabels = {
  human: string
  manual: string
  restore: string
  unknownMeeting: string
}

/**
 * One entry in the AI version's history for the Context versions UI.
 *
 * Mapped from `@ai-meeting/shared` list/detail DTOs so cards keep a stable
 * view model (`sourceLabel`, `changes`) while the API uses server names.
 *
 * Every listed row snapshots the AI version; the only rows that cannot be
 * restored are the legacy `manual` description snapshots, which the server
 * hides too.
 */
export interface ProjectContextVersion {
  id: string
  label: string
  sourceKind: ProjectContextVersionSourceKind
  sourceLabel: string
  sourceIsMeeting: boolean
  meetingId: string | null
  createdAt: string
  changes: string
  active: boolean
  comparesTo?: string
  segments?: ProjectContextDiffSegment[]
  /** Present after a detail fetch: the AI version at file time. */
  text?: string | null
  general?: ProjectContextGeneralFact[]
}

/** Every listed row is an AI version; legacy description rows stay read-only. */
export function canRestoreProjectContextVersion(
  version: Pick<ProjectContextVersion, 'active' | 'sourceKind'>
): boolean {
  return !version.active && version.sourceKind !== 'manual'
}

function sourceFromItem(
  sourceKind: ProjectContextVersionSourceKind,
  meetingId: string | null,
  meetingLabel: string | null,
  labels: ProjectContextVersionSourceLabels
): { sourceIsMeeting: boolean; sourceLabel: string } {
  if (sourceKind === 'meeting') {
    return {
      sourceIsMeeting: Boolean(meetingId),
      sourceLabel: meetingLabel?.trim() ? meetingLabel : labels.unknownMeeting,
    }
  }
  if (sourceKind === 'human') {
    return { sourceIsMeeting: false, sourceLabel: labels.human }
  }
  if (sourceKind === 'restore') {
    return { sourceIsMeeting: false, sourceLabel: labels.restore }
  }
  return { sourceIsMeeting: false, sourceLabel: labels.manual }
}

export function mapVersionListItem(
  item: ProjectContextVersionListItem,
  labels: ProjectContextVersionSourceLabels
): ProjectContextVersion {
  const { sourceIsMeeting, sourceLabel } = sourceFromItem(
    item.sourceKind,
    item.meetingId,
    item.meetingLabel,
    labels
  )
  return {
    id: item.id,
    label: item.label,
    sourceKind: item.sourceKind,
    sourceLabel,
    sourceIsMeeting,
    meetingId: item.meetingId,
    createdAt: item.createdAt,
    changes: item.changesSummary,
    active: item.active,
  }
}

export function mapVersionDetail(
  detail: ProjectContextVersionDetailResponse,
  labels: ProjectContextVersionSourceLabels
): ProjectContextVersion {
  const { sourceIsMeeting, sourceLabel } = sourceFromItem(
    detail.sourceKind,
    detail.meetingId,
    detail.meetingLabel,
    labels
  )
  return {
    id: detail.id,
    label: detail.label,
    sourceKind: detail.sourceKind,
    sourceLabel,
    sourceIsMeeting,
    meetingId: detail.meetingId,
    createdAt: detail.createdAt,
    changes: detail.changesSummary,
    active: false,
    comparesTo: detail.comparesTo ?? undefined,
    segments: detail.segments,
    text: detail.text,
    general: detail.general,
  }
}
